import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const MAX_QTY = 10;
const STORAGE_KEY = 'nk-shop-cart';

export type CartLine = {
  productId: string;
  size: string;
  qty: number;
};

type CartState = {
  lines: CartLine[];
  add: (productId: string, size: string) => void;
  changeQty: (productId: string, size: string, delta: number) => void;
  setQty: (productId: string, size: string, qty: number) => void;
  remove: (productId: string, size: string) => void;
  clear: () => void;
  reconcile: (validIds: Set<string>, sizesById: Map<string, string[]>) => void;
};

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],

      add: (productId, size) =>
        set((state) => {
          const existing = state.lines.find(
            (line) => line.productId === productId && line.size === size,
          );
          if (existing) {
            return {
              lines: state.lines.map((line) =>
                line === existing
                  ? { ...line, qty: Math.min(line.qty + 1, MAX_QTY) }
                  : line,
              ),
            };
          }
          return { lines: [...state.lines, { productId, size, qty: 1 }] };
        }),

      changeQty: (productId, size, delta) => {
        const line = get().lines.find(
          (entry) => entry.productId === productId && entry.size === size,
        );
        if (!line) return;
        if (line.qty + delta < 1) {
          get().remove(productId, size);
          return;
        }
        set((state) => ({
          lines: state.lines.map((entry) =>
            entry === line
              ? { ...entry, qty: Math.min(entry.qty + delta, MAX_QTY) }
              : entry,
          ),
        }));
      },

      setQty: (productId, size, qty) =>
        set((state) => ({
          lines: state.lines.map((entry) =>
            entry.productId === productId && entry.size === size
              ? { ...entry, qty: Math.max(1, Math.min(qty, MAX_QTY)) }
              : entry,
          ),
        })),

      remove: (productId, size) =>
        set((state) => ({
          lines: state.lines.filter(
            (entry) => !(entry.productId === productId && entry.size === size),
          ),
        })),

      clear: () => set({ lines: [] }),

      /**
       * Drop lines that no longer exist, went inactive, or reference a size the
       * product no longer offers. Runs whenever the catalog loads, mirroring the
       * original `readCart` behaviour.
       */
      reconcile: (validIds, sizesById) =>
        set((state) => ({
          lines: state.lines.filter((line) => {
            if (!validIds.has(line.productId)) return false;
            const sizes = sizesById.get(line.productId) ?? [];
            return sizes.length === 0 || sizes.includes(line.size);
          }),
        })),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      partialize: (state) => ({ lines: state.lines }),
    },
  ),
);
